import React from "react";
import {
  ClockIcon,
  BanknotesIcon,
  CubeIcon,
  PowerIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  ArrowDownTrayIcon,
  LockOpenIcon,
  LockClosedIcon,
  BoltIcon,
  BoltSlashIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/outline";

interface AtmSourceTagProps {
  source: string | null;
}

interface SourceTagConfig {
  color: string;
  textColor: string;
  text: string;
  icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> & {
      title?: string;
      titleId?: string;
    } & React.RefAttributes<SVGSVGElement>
  >;
}

// The new ATM firmware renamed several statuses from the old firmware. We map
// each old spelling to its new (canonical) one so the same event renders the
// same way regardless of firmware version while the fleet is being migrated.
const sourceAliases: Record<string, string> = {
  cashout: "cash-out",
  "printer-low": "paper-low",
  "printer-out": "paper-empty",
  "cassette-out": "cashbox-removed",
  "cassette-in": "cashbox-replaced",
};

/**
 * Normalizes a raw source to its canonical key (old-firmware spellings mapped
 * to their new equivalent). Defaults to "check-in" when null.
 * @param source Raw source value.
 * @returns Canonical source key.
 */
const resolveSourceKey = (source: string | null): string => {
  const rawKey = (source ?? "check-in").toLowerCase();
  return sourceAliases[rawKey] ?? rawKey;
};

/**
 * Spanish label for an ATM log source (handles old and new firmware spellings).
 * @param source Raw source value.
 * @returns Localized label, or the raw value if unmapped.
 */
export const getAtmSourceLabel = (source: string | null): string => {
  const sourceKey = resolveSourceKey(source);
  return tagConfig[sourceKey]?.text ?? sourceKey;
};

/**
 * Renders a colored tag for an ATM log source, normalizing old-firmware
 * spellings and falling back to a neutral tag for unknown sources.
 * @param source Raw source value from the ATM log (may be null).
 * @returns The tag element for the given source.
 */
export const AtmSourceTag = ({ source }: AtmSourceTagProps) => {
  const sourceKey = resolveSourceKey(source);

  // Unmapped sources render as a neutral tag showing the raw value, instead of
  // being mislabeled as Check-in.
  const config: SourceTagConfig = tagConfig[sourceKey] ?? {
    color: "bg-gray-100",
    textColor: "text-gray-700",
    text: sourceKey,
    icon: QuestionMarkCircleIcon,
  };
  const IconComponent = config.icon;

  return (
    <div
      className={`rounded-full px-3 py-1 ${config.color} flex w-fit items-center justify-center gap-1`}
    >
      <IconComponent className={`h-4 w-4 ${config.textColor}`} />
      <span className={`text-sm font-medium ${config.textColor}`}>
        {config.text}
      </span>
    </div>
  );
};

const tagConfig: Record<string, SourceTagConfig> = {
  "check-in": {
    color: "bg-blue-100",
    textColor: "text-blue-700",
    text: "Check-in",
    icon: ClockIcon,
  },
  // Cash deposit (new firmware). Not equivalent to "check-in".
  "cash-in": {
    color: "bg-emerald-100",
    textColor: "text-emerald-700",
    text: "Ingreso de efectivo",
    icon: ArrowDownTrayIcon,
  },
  // Cash cut. Old firmware: "cashout" (normalized to "cash-out").
  "cash-out": {
    color: "bg-green-100",
    textColor: "text-green-700",
    text: "Corte de caja",
    icon: BanknotesIcon,
  },
  // Cash container. Old firmware: "cassette-out"/"cassette-in".
  "cashbox-removed": {
    color: "bg-amber-100",
    textColor: "text-amber-700",
    text: "Cashbox retirado",
    icon: CubeIcon,
  },
  "cashbox-replaced": {
    color: "bg-lime-100",
    textColor: "text-lime-700",
    text: "Cashbox colocado",
    icon: CubeIcon,
  },
  // Printer. Old firmware: "printer-low"/"printer-out".
  "paper-low": {
    color: "bg-yellow-100",
    textColor: "text-yellow-700",
    text: "Papel bajo",
    icon: PrinterIcon,
  },
  "paper-empty": {
    color: "bg-orange-100",
    textColor: "text-orange-700",
    text: "Sin papel",
    icon: ExclamationTriangleIcon,
  },
  "printer-ok": {
    color: "bg-teal-100",
    textColor: "text-teal-700",
    text: "Impresora OK",
    icon: PrinterIcon,
  },
  "power-on": {
    color: "bg-sky-100",
    textColor: "text-sky-700",
    text: "Encendido",
    icon: PowerIcon,
  },
  "power-off": {
    color: "bg-gray-100",
    textColor: "text-gray-700",
    text: "Apagado",
    icon: PowerIcon,
  },
  exit: {
    color: "bg-gray-100",
    textColor: "text-gray-700",
    text: "Salida",
    icon: ArrowRightOnRectangleIcon,
  },
  // Power supply (new firmware). Kept separate from the old-firmware
  // "power-off"/"power-on" until the equivalence is confirmed.
  power_connected: {
    color: "bg-cyan-100",
    textColor: "text-cyan-700",
    text: "Con alimentación",
    icon: BoltIcon,
  },
  power_disconnected: {
    color: "bg-rose-100",
    textColor: "text-rose-700",
    text: "Sin alimentación",
    icon: BoltSlashIcon,
  },
  // ATM door and lock (new firmware).
  "door-open": {
    color: "bg-fuchsia-100",
    textColor: "text-fuchsia-700",
    text: "Puerta abierta",
    icon: LockOpenIcon,
  },
  "door-closed": {
    color: "bg-indigo-100",
    textColor: "text-indigo-700",
    text: "Puerta cerrada",
    icon: LockClosedIcon,
  },
  "doorlock-open": {
    color: "bg-pink-100",
    textColor: "text-pink-700",
    text: "Chapa abierta",
    icon: LockOpenIcon,
  },
  "doorlock-closed": {
    color: "bg-violet-100",
    textColor: "text-violet-700",
    text: "Chapa cerrada",
    icon: LockClosedIcon,
  },
  // Security alert: sudden movement of the ATM (new firmware).
  security_alert: {
    color: "bg-red-100",
    textColor: "text-red-700",
    text: "Alerta de seguridad",
    icon: ShieldExclamationIcon,
  },
  "card-inventory": {
    color: "bg-purple-100",
    textColor: "text-purple-700",
    text: "Inventario de tarjetas",
    icon: CreditCardIcon,
  },
};
